// Mirrors the backend Pydantic shapes from `src/scaicrm/entities/*/schemas.py`.
// Hand-maintained for P1; regenerate from the FastAPI OpenAPI schema once the
// codegen toolchain is in place (P1 or P2 task).

export type AccountListItem = {
  id: string;
  name: string;
  kind: string;
  primary_domain: string | null;
  primary_email: string | null;
  lifecycle_stage: string;
  owner_user_id: string | null;
  tags: string[] | null;
  version: number;
  updated_at: string;
};

export type AccountListPage = {
  items: AccountListItem[];
  next_cursor: string | null;
  total_estimate: number | null;
};

export type AccountRead = AccountListItem & {
  workspace_id: string;
  legal_name: string | null;
  website: string | null;
  additional_domains: string[] | null;
  primary_phone: string | null;
  industry: string | null;
  employee_count_band: string | null;
  annual_revenue_band: string | null;
  annual_revenue_amount: number | null;
  annual_revenue_currency: string | null;
  billing_address: Record<string, unknown> | null;
  shipping_address: Record<string, unknown> | null;
  description: string | null;
  parent_account_id: string | null;
  scaikey_partner_id: string | null;
  scaikey_tenant_id: string | null;
  scaicontrol_customer_id: string | null;
  external_refs: Record<string, unknown> | null;
  tags: string[] | null;
  metadata: Record<string, unknown> | null;
  custom_fields: Record<string, unknown> | null;
  merged_into: string | null;
  created_at: string;
  archived_at: string | null;
  version: number;
};

export type AccountCreate = {
  name: string;
  legal_name?: string | null;
  kind?: string;
  website?: string | null;
  primary_domain?: string | null;
  primary_email?: string | null;
  primary_phone?: string | null;
  industry?: string | null;
  lifecycle_stage?: string;
  owner_user_id?: string | null;
  tags?: string[] | null;
  custom_fields?: Record<string, unknown> | null;
};

export type AccountUpdate = Partial<AccountCreate> & {
  description?: string | null;
};

export type ContactListItem = {
  id: string;
  full_name: string;
  display_name: string | null;
  primary_email: string | null;
  primary_account_id: string | null;
  lifecycle_stage: string;
  do_not_contact: boolean;
  owner_user_id: string | null;
  tags: string[] | null;
  // AI-derived; null until the lead_scorer has run on this contact.
  lead_score: number | null;
  version: number;
  updated_at: string;
};

export type ContactListPage = {
  items: ContactListItem[];
  next_cursor: string | null;
  total_estimate: number | null;
};

export type ContactRead = ContactListItem & {
  workspace_id: string;
  first_name: string | null;
  last_name: string | null;
  additional_emails: string[] | null;
  primary_phone: string | null;
  additional_phones: string[] | null;
  title: string | null;
  seniority: string | null;
  department: string | null;
  lead_source: string | null;
  lead_source_detail: string | null;
  linkedin_url: string | null;
  timezone: string | null;
  locale: string | null;
  do_not_contact_reason: string | null;
  do_not_contact_set_at: string | null;
  email_subscription_state: string;
  gdpr_consent_state: Record<string, unknown> | null;
  birthday: string | null;
  address: Record<string, unknown> | null;
  linked_user_id: string | null;
  external_refs: Record<string, unknown> | null;
  tags: string[] | null;
  metadata: Record<string, unknown> | null;
  custom_fields: Record<string, unknown> | null;
  merged_into: string | null;
  lead_score_reasoning: string | null;
  lead_score_at: string | null;
  lead_score_model: string | null;
  created_at: string;
  archived_at: string | null;
  version: number;
};

export type ContactCreate = {
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  primary_email?: string | null;
  primary_phone?: string | null;
  primary_account_id?: string | null;
  title?: string | null;
  lifecycle_stage?: string;
  do_not_contact?: boolean;
  owner_user_id?: string | null;
  tags?: string[] | null;
  custom_fields?: Record<string, unknown> | null;
};

export type ContactUpdate = Partial<ContactCreate>;

export type SearchNoteHit = {
  id: string;
  body: string;
  attached_to_entity_type: AttachableEntity;
  attached_to_entity_id: string;
  // Set only for custom-record attachments — lets the UI render a deep
  // link to /custom/<key>/<id>.
  attached_object_key: string | null;
  created_at: string;
};

export type SearchActivityHit = {
  id: string;
  kind: string;
  subject: string;
  body: string | null;
  primary_entity_type: AttachableEntity;
  primary_entity_id: string;
  // Same role as SearchNoteHit.attached_object_key.
  primary_object_key: string | null;
  started_at: string;
};

export type SearchCustomRecordHit = {
  id: string;
  name: string;
  // Server-rendered display name (see CustomRecordRead.display_name).
  display_name: string;
  custom_object_definition_id: string;
  object_key: string;
  object_label_singular: string;
  updated_at: string;
};

export type SearchResults = {
  accounts: AccountListItem[];
  contacts: ContactListItem[];
  custom_records: SearchCustomRecordHit[];
  notes: SearchNoteHit[];
  activities: SearchActivityHit[];
};

// ── Notes ─────────────────────────────────────────────────────────────────────

export type NoteVisibility = "workspace" | "private" | "restricted";
// `custom_record` was added when notes & activities became attachable to
// user-declared custom-object records. The `also_attached_to` picker UI
// stays limited to built-ins for now — cross-object linking is its own
// UX exercise.
export type AttachableEntity = "account" | "contact" | "deal" | "custom_record";

export type NoteAttachmentRef = {
  entity_type: AttachableEntity;
  entity_id: string;
};

export type NoteRead = {
  id: string;
  workspace_id: string;
  body: string;
  pinned: boolean;
  attached_to_entity_type: AttachableEntity;
  attached_to_entity_id: string;
  also_attached_to: NoteAttachmentRef[] | null;
  mentions: string[] | null;
  visibility: NoteVisibility;
  restricted_role_ids: string[] | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  version: number;
};

export type NoteCreate = {
  body: string;
  pinned?: boolean;
  attached_to_entity_type: AttachableEntity;
  attached_to_entity_id: string;
  also_attached_to?: NoteAttachmentRef[];
  mentions?: string[];
  visibility?: NoteVisibility;
  restricted_role_ids?: string[];
};

export type NoteUpdate = {
  body?: string;
  pinned?: boolean;
  visibility?: NoteVisibility;
  restricted_role_ids?: string[];
  mentions?: string[];
  also_attached_to?: NoteAttachmentRef[];
};

export type NoteListPage = {
  items: NoteRead[];
  next_cursor: string | null;
  total_estimate: number | null;
};

// ── Activities ───────────────────────────────────────────────────────────────

export type ActivityKind = "meeting" | "call" | "email" | "task" | "system_event";
export type ActivityStatus = "planned" | "completed" | "cancelled" | "overdue";

export type ActivityParticipant = {
  kind: "contact" | "user";
  id: string;
};

export type ActivityRead = {
  id: string;
  workspace_id: string;
  kind: ActivityKind;
  subject: string;
  body: string | null;
  direction: "inbound" | "outbound" | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  status: ActivityStatus;
  due_date: string | null;
  completed_at: string | null;
  outcome: string | null;
  participants: ActivityParticipant[] | null;
  primary_entity_type: AttachableEntity;
  primary_entity_id: string;
  source_system: string;
  source_ref: string | null;
  metadata: Record<string, unknown> | null;
  custom_fields: Record<string, unknown> | null;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  version: number;
};

export type ActivityCreate = {
  kind: ActivityKind;
  subject: string;
  body?: string;
  direction?: "inbound" | "outbound";
  started_at?: string;
  ended_at?: string;
  duration_seconds?: number;
  status?: ActivityStatus;
  due_date?: string;
  outcome?: string;
  participants?: ActivityParticipant[];
  custom_fields?: Record<string, unknown> | null;
  primary_entity_type: AttachableEntity;
  primary_entity_id: string;
};

export type ActivityUpdate = {
  subject?: string;
  body?: string | null;
  direction?: "inbound" | "outbound" | null;
  started_at?: string | null;
  ended_at?: string | null;
  duration_seconds?: number | null;
  status?: ActivityStatus;
  due_date?: string | null;
  outcome?: string | null;
  participants?: ActivityParticipant[];
  custom_fields?: Record<string, unknown> | null;
};

export type ActivityListPage = {
  items: ActivityRead[];
  next_cursor: string | null;
  total_estimate: number | null;
};

// ── Subscriptions ────────────────────────────────────────────────────────────

export type SubscriptionState = "trial" | "active" | "past_due" | "cancelled" | "paused";
export type SubscriptionKind = "single" | "pack";

export type PackInclude = {
  service_slug: string;
  plan_id: string;
};

export type SubscriptionRead = {
  id: string;
  scaicontrol_customer_id: string;
  scaicontrol_subscription_id: string;
  partner_id: string | null;
  tenant_id: string | null;
  subscription_kind: SubscriptionKind;
  pack_includes: PackInclude[] | null;
  state: SubscriptionState;
  plan_key: string;
  plan_name: string;
  quantity: number;
  mrr_amount: number | null;
  mrr_currency: string | null;
  arr_amount: number | null;
  arr_currency: string | null;
  trial_end_date: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  next_billing_date: string | null;
  cancelled_at: string | null;
  pending_cancellation_at: string | null;
  cancellation_reason: string | null;
  payment_status: string | null;
  feature_flags: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  sync_status: string;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  version: number;
};

export type SubscriptionListResponse = {
  items: SubscriptionRead[];
};

// ── ScaiControl plan catalogue + actions ─────────────────────────────────────

export type PlanCatalogueEntry = {
  id: string;
  slug: string | null;
  name: string | null;
  service_id: string | null;
  service_slug: string | null;
  tier: string | null;
  billing_period: string | null;
  base_price_cents: number | null;
  currency: string | null;
  trial_days: number | null;
  raw: Record<string, unknown> | null;
};

export type PlanCatalogueResponse = {
  plans: PlanCatalogueEntry[];
};

export type SubscriptionActionResponse = {
  action: string;
  subscription_id: string;
  scaicontrol_subscription_id: string;
  reason: string | null;
  submitted_at: string;
  new_state: string | null;
  new_plan_id: string | null;
  new_plan_key: string | null;
  new_plan_name: string | null;
  scaicontrol_request_id: string | null;
  note: string;
};

export type ChangePlanRequest = {
  plan_id: string;
  reason?: string | null;
};

export type CancelSubscriptionRequest = {
  immediate?: boolean;
  reason?: string | null;
};

export type SubscriptionHistoryEntry = {
  action: string | null;
  user_id: string | null;
  created_at: string | null;
  details: Record<string, unknown> | null;
  raw: Record<string, unknown> | null;
};

export type SubscriptionHistoryResponse = {
  items: SubscriptionHistoryEntry[];
};

export type BulkAction = "cancel" | "cancel_immediate" | "suspend" | "resume";

export type BulkActionRequest = {
  subscription_ids: string[];
  action: BulkAction;
  reason?: string | null;
};

export type BulkActionItem = {
  subscription_id: string | null;
  scaicontrol_subscription_id: string;
  error: string | null;
  code: string | null;
};

export type BulkActionResponse = {
  action: string;
  reason: string | null;
  total: number;
  succeeded_count: number;
  failed_count: number;
  succeeded: BulkActionItem[];
  failed: BulkActionItem[];
  scaicontrol_request_id: string | null;
  submitted_at: string;
  note: string;
};

// ── Custom fields ────────────────────────────────────────────────────────────

export type CustomFieldEntityType = "account" | "contact" | "deal" | "product" | "activity";
export type CustomFieldType =
  | "text"
  | "number"
  | "date"
  | "select"
  | "multi_select"
  | "lookup"
  | "email"
  | "url"
  | "phone"
  | "rating"
  | "currency";
// `product` was added alongside the Products UI (015_lookup_product); `deal`
// followed once the DealPicker landed (016_lookup_deal).
export type CustomFieldLookupTarget =
  | "account"
  | "contact"
  | "user"
  | "product"
  | "deal"
  | "custom_record";

export type CustomFieldDefinitionRead = {
  id: string;
  // Either entity_type (built-in) or custom_object_definition_id (custom
  // object) is set — never both, enforced server-side.
  entity_type: CustomFieldEntityType | null;
  custom_object_definition_id: string | null;
  key: string;
  label: string;
  field_type: CustomFieldType;
  options: string[] | null;
  lookup_entity_type: CustomFieldLookupTarget | null;
  // Required when lookup_entity_type='custom_record'; null otherwise.
  lookup_custom_object_definition_id: string | null;
  required: boolean;
  help_text: string | null;
  order_index: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  version: number;
};

export type CustomFieldDefinitionCreate = {
  entity_type?: CustomFieldEntityType | null;
  custom_object_definition_id?: string | null;
  key: string;
  label: string;
  field_type: CustomFieldType;
  options?: string[] | null;
  lookup_entity_type?: CustomFieldLookupTarget | null;
  lookup_custom_object_definition_id?: string | null;
  required?: boolean;
  help_text?: string | null;
  order_index?: number | null;
};

export type CustomFieldDefinitionUpdate = {
  label?: string | null;
  options?: string[] | null;
  required?: boolean | null;
  help_text?: string | null;
};

export type CustomFieldValues = Record<
  string,
  string | number | string[] | { amount: number; currency: string } | null
>;

// ── Custom Objects (schema-registry — definitions layer) ─────────────────────

export type CustomObjectDefinitionRead = {
  id: string;
  key: string;
  label_singular: string;
  label_plural: string;
  description: string | null;
  // Mustache-style display-name template (e.g. "{{sku}} — {{name}}").
  // UI substitutes against record.data + record itself. Null → fall back
  // to record.name.
  name_template: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  version: number;
};

export type CustomObjectDefinitionCreate = {
  key: string;
  label_singular: string;
  label_plural: string;
  description?: string | null;
  name_template?: string | null;
};

export type CustomObjectDefinitionUpdate = {
  label_singular?: string | null;
  label_plural?: string | null;
  description?: string | null;
  name_template?: string | null;
};

export type CustomObjectDefinitionList = {
  items: CustomObjectDefinitionRead[];
};

// ── Agents + AgentAction lifecycle ──────────────────────────────────────────

export type AgentRead = {
  id: string;
  workspace_id: string;
  key: string;
  name: string;
  description: string | null;
  kind: string;
  builtin_kind: string;
  enabled: boolean;
  preferred_model_id: string | null;
  system_prompt_override: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  version: number;
};

export type AgentList = { items: AgentRead[] };

export type AgentUpdate = {
  name?: string;
  description?: string | null;
  enabled?: boolean;
  preferred_model_id?: string | null;
  system_prompt_override?: string | null;
};

export type AgentActionState =
  | "running"
  | "succeeded"
  | "failed"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "superseded";

export type AgentActionRead = {
  id: string;
  workspace_id: string;
  agent_id: string;
  kind: string;
  state: AgentActionState;
  subject_entity_type: string | null;
  // Bare ULID body (no prefix). UI re-prefixes when building links.
  subject_entity_id: string | null;
  // Set only when subject_entity_type='custom_record' AND the parent
  // CustomObjectDefinition still exists. Required by entityDetailLink
  // to route to /custom/<key>/<id>; otherwise the row renders unlinked.
  subject_object_key: string | null;
  actor_kind: string;
  actor_id: string;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  error: string | null;
  model_used: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  started_at: string;
  completed_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  rejection_reason: string | null;
};

export type InvokeBuiltinResponse = {
  agent_id: string;
  builtin_kind: string;
  summary: Record<string, number>;
};

export type BulkApproveItemResult = {
  action_id: string;
  ok: boolean;
  state: string | null;
  error: string | null;
};

export type BulkApproveResponse = {
  total: number;
  succeeded: number;
  failed: number;
  items: BulkApproveItemResult[];
};

export type AgentActionPage = {
  items: AgentActionRead[];
  next_cursor: string | null;
};

export type AgentActionParams = {
  cursor?: string;
  limit?: number;
  kind?: string;
  state?: string;
  subject_entity_type?: string;
  subject_entity_id?: string;
  actor_id?: string;
};

export type DraftEmailRequest = {
  purpose: string;
  tone?: "friendly" | "professional" | "concise" | "warm" | null;
  context_account_id?: string | null;
  context_deal_id?: string | null;
};

export type DraftEmailResponse = {
  subject: string;
  body: string;
  model: string;
  input_tokens: number | null;
  output_tokens: number | null;
  action_id: string;
};

export type LeadScoreResponse = {
  score: number;
  reasoning: string;
  model: string;
  input_tokens: number | null;
  output_tokens: number | null;
  action_id: string;
};

export type QaSource = {
  entity_type: string;
  // Bare ULID body — UI re-prefixes when building links.
  entity_id: string;
  label: string;
};

export type QaResponse = {
  answer: string;
  model: string;
  input_tokens: number | null;
  output_tokens: number | null;
  sources: QaSource[];
  action_id: string;
};

// Shared response shape across account/contact/deal :generate_brief.
// `context_counts` keys vary per entity type (activities/notes/contacts/
// open_deals for account; activities/notes/line_items for deal); the UI
// iterates the dict for display.
export type BriefResponse = {
  brief: string;
  model: string;
  input_tokens: number | null;
  output_tokens: number | null;
  context_counts: Record<string, number>;
  action_id: string;
};

// ── AI settings (workspace tier; partner tier is operator-only for v1.x) ───

export type WorkspaceAISettingsRow = {
  id: string;
  workspace_id: string;
  allowed_model_ids: string[] | null;
  default_model_id: string | null;
  monthly_inference_budget_cents: number | null;
  created_at: string;
  updated_at: string;
  version: number;
};

export type EffectiveAISettings = {
  allowed_model_ids: string[] | null;
  default_model_id: string | null;
  ai_enabled: boolean;
  partner_allowed_model_ids: string[] | null;
  partner_default_model_id: string | null;
  workspace_allowed_model_ids: string[] | null;
  workspace_default_model_id: string | null;
};

export type WorkspaceAISettingsResponse = {
  workspace: WorkspaceAISettingsRow | null;
  effective: EffectiveAISettings;
};

export type WorkspaceAISettingsUpdate = {
  allowed_model_ids: string[] | null;
  default_model_id: string | null;
  monthly_inference_budget_cents?: number | null;
};

export type AIModelCatalogEntry = {
  id: string;
  label: string;
  vendor: string | null;
  family: string | null;
  context_window: number | null;
  input_price_per_1k_cents: number | null;
  output_price_per_1k_cents: number | null;
};

export type AIModelCatalogResponse = {
  models: AIModelCatalogEntry[];
  scaigrid_configured: boolean;
};

// ── Audit log ─────────────────────────────────────────────────────────────────

export type AuditLogRead = {
  id: string;
  entity_type: string;
  entity_id: string;
  // Populated only when entity_type='custom_record' AND the parent object
  // still exists. Lets the UI render the right detail-page link.
  object_key: string | null;
  action: string;
  actor_kind: string;
  actor_id: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  trace_id: string | null;
  created_at: string;
};

export type AuditLogPage = {
  items: AuditLogRead[];
  next_cursor: string | null;
};

export type AuditLogParams = {
  cursor?: string;
  limit?: number;
  entity_type?: string;
  entity_id?: string;
  actor_kind?: string;
  actor_id?: string;
  action?: string;
  since?: string;
  until?: string;
};

// ── Custom Records (data layer) ──────────────────────────────────────────────

export type CustomRecordRead = {
  id: string;
  custom_object_definition_id: string;
  name: string;
  // Server-rendered name_template applied to (name, data). Equals `name`
  // when the parent object has no template OR the template substitution
  // collapses to blank. Components should prefer this for display.
  display_name: string;
  data: Record<string, unknown> | null;
  tags: string[] | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  version: number;
};

export type CustomRecordCreate = {
  name: string;
  data?: Record<string, unknown> | null;
  tags?: string[] | null;
};

export type CustomRecordUpdate = {
  name?: string | null;
  data?: Record<string, unknown> | null;
  tags?: string[] | null;
};

export type CustomRecordListPage = {
  items: CustomRecordRead[];
  next_cursor: string | null;
};

// ── Pipeline / Stage / Deal ───────────────────────────────────────────────────

export type StageType = "open" | "won" | "lost";
export type DealStatus = "open" | "won" | "lost" | "abandoned";

export type PipelineRead = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  is_default: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  version: number;
};

export type StageRead = {
  id: string;
  pipeline_id: string;
  key: string;
  name: string;
  stage_type: StageType;
  probability_pct: number;
  order_index: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  version: number;
};

export type DealListItem = {
  id: string;
  name: string;
  pipeline_id: string;
  stage_id: string;
  status: DealStatus;
  amount: number | null;
  currency: string | null;
  expected_close_date: string | null;
  owner_user_id: string | null;
  account_id: string | null;
  tags: string[] | null;
  version: number;
  updated_at: string;
};

export type DealListPage = {
  items: DealListItem[];
  next_cursor: string | null;
};

export type DealRead = DealListItem & {
  description: string | null;
  primary_contact_id: string | null;
  probability_pct: number | null;
  actual_close_date: string | null;
  lost_reason: string | null;
  lost_reason_detail: string | null;
  next_step: string | null;
  next_step_due: string | null;
  source: string | null;
  tags: string[] | null;
  external_refs: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  custom_fields: Record<string, unknown> | null;
  archived_at: string | null;
  created_at: string;
  version: number;
};

export type DealCreate = {
  name: string;
  description?: string | null;
  account_id?: string | null;
  primary_contact_id?: string | null;
  pipeline_id?: string | null;
  stage_id?: string | null;
  owner_user_id?: string | null;
  amount?: number | null;
  currency?: string | null;
  probability_pct?: number | null;
  expected_close_date?: string | null;
  next_step?: string | null;
  next_step_due?: string | null;
  source?: string | null;
  tags?: string[] | null;
  custom_fields?: Record<string, unknown> | null;
};

export type DealUpdate = Partial<Omit<DealCreate, "pipeline_id" | "stage_id">>;

export type DealListParams = {
  pipeline_id?: string;
  stage_id?: string;
  status?: DealStatus;
  owner_user_id?: string;
  account_id?: string;
  contact_id?: string;
  q?: string;
  limit?: number;
  // Match the dashboard "stalled" badge: open deals with no updated_at
  // movement in N days.
  stalled_days?: number;
};

export type PipelineCreate = {
  key: string;
  name: string;
  description?: string | null;
  is_default?: boolean;
};

export type PipelineUpdate = {
  name?: string;
  description?: string | null;
};

export type StageCreate = {
  key: string;
  name: string;
  stage_type?: StageType;
  probability_pct?: number;
  order_index?: number;
};

export type StageUpdate = {
  name?: string;
  stage_type?: StageType;
  probability_pct?: number;
};

// ── Product (catalogue) ───────────────────────────────────────────────────────

export type ProductListItem = {
  id: string;
  name: string;
  sku: string | null;
  unit_price: number | null;
  currency: string | null;
  active: boolean;
  version: number;
  updated_at: string;
};

export type ProductListPage = {
  items: ProductListItem[];
  next_cursor: string | null;
};

export type ProductRead = {
  id: string;
  name: string;
  sku: string | null;
  description: string | null;
  unit_price: number | null;
  currency: string | null;
  active: boolean;
  tags: string[] | null;
  custom_fields: Record<string, unknown> | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  version: number;
};

export type ProductCreate = {
  name: string;
  sku?: string | null;
  description?: string | null;
  unit_price?: number | null;
  currency?: string | null;
  active?: boolean;
  tags?: string[] | null;
  custom_fields?: Record<string, unknown> | null;
};

export type ProductUpdate = Partial<ProductCreate>;

export type ProductListParams = {
  q?: string;
  active?: boolean;
  include_archived?: boolean;
  limit?: number;
};

// ── Deal line items ──────────────────────────────────────────────────────────

export type DealLineItemRead = {
  id: string;
  deal_id: string;
  product_id: string | null;
  name: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  discount_pct: number;
  line_total: number;
  currency: string | null;
  position: number;
  created_at: string;
  updated_at: string;
  version: number;
};

export type DealLineItemList = {
  items: DealLineItemRead[];
  subtotal: number;
  currency: string | null;
};

export type DealLineItemCreate = {
  product_id?: string | null;
  name?: string | null;
  description?: string | null;
  quantity?: number;
  unit_price?: number | null;
  discount_pct?: number;
  currency?: string | null;
  position?: number | null;
};

export type DealLineItemUpdate = {
  name?: string | null;
  description?: string | null;
  quantity?: number;
  unit_price?: number;
  discount_pct?: number;
  currency?: string | null;
  position?: number;
};

// ── Workspace ─────────────────────────────────────────────────────────────────

export type WorkspaceRead = {
  id: string;
  tenant_id: string;
  partner_id: string;
  key: string;
  name: string;
  description: string | null;
  default_locale: string;
  default_currency: string;
  default_timezone: string;
  is_internal: boolean;
  read_only: boolean;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  version: number;
};

// ── Workspace roles ──────────────────────────────────────────────────────────

export type WorkspaceRoleRead = {
  id: string;
  workspace_id: string;
  key: string;
  name: string;
  description: string | null;
  origin: string;
  level: string;
};

// ── Saved views ──────────────────────────────────────────────────────────────

export type SavedViewEntityType =
  | "account"
  | "contact"
  | "deal"
  | "task"
  | "product"
  | "custom_record";

export type SavedViewRead = {
  id: string;
  workspace_id: string;
  user_id: string;
  entity_type: SavedViewEntityType;
  // Set iff entity_type='custom_record'. Backend's CHECK enforces the XOR.
  custom_object_definition_id: string | null;
  name: string;
  filters: Record<string, unknown>;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  version: number;
};

export type SavedViewCreate = {
  entity_type: SavedViewEntityType;
  custom_object_definition_id?: string | null;
  name: string;
  filters: Record<string, unknown>;
  is_default?: boolean;
};

export type SavedViewUpdate = {
  name?: string;
  filters?: Record<string, unknown>;
};

export type SavedViewList = { items: SavedViewRead[] };

// ── Dashboard ────────────────────────────────────────────────────────────────

export type DashboardTasks = {
  my_planned: number;
  my_overdue: number;
  my_due_today: number;
};

export type DashboardDealsByCurrency = {
  currency: string;
  total_amount: number;
  // Probability-weighted forecast: sum(amount * probability_pct/100).
  forecast_amount: number;
};

export type DashboardDealsByStage = {
  stage_id: string;
  stage_name: string;
  stage_type: string;
  count: number;
  total_amount: number;
  forecast_amount: number;
};

export type DashboardStalledTotal = {
  currency: string;
  total_amount: number;
};

export type DashboardForecastMonth = {
  // YYYY-MM, or the literal "unscheduled" for deals with no expected close.
  month: string;
  currency: string;
  weighted_amount: number;
  count: number;
};

export type DashboardDeals = {
  open_count: number;
  open_total_by_currency: DashboardDealsByCurrency[];
  default_pipeline_id: string | null;
  default_pipeline_name: string | null;
  by_stage: DashboardDealsByStage[];
  stalled_days: number;
  stalled_count: number;
  stalled_total_by_currency: DashboardStalledTotal[];
  forecast_by_month: DashboardForecastMonth[];
};

export type DashboardMyCounts = {
  accounts: number;
  contacts: number;
  deals: number;
};

export type DashboardClosedDealsTotal = {
  currency: string;
  won_amount: number;
  lost_amount: number;
};

export type DashboardClosedDeals = {
  window_days: number;
  won_count: number;
  lost_count: number;
  win_rate_pct: number | null;
  avg_days_to_close: number | null;
  totals_by_currency: DashboardClosedDealsTotal[];
};

export type DashboardResponse = {
  tasks: DashboardTasks;
  deals: DashboardDeals;
  closed_deals: DashboardClosedDeals;
  my_counts: DashboardMyCounts;
};

export type ApiError = {
  error: {
    code: string;
    message: string;
    details: Record<string, unknown>;
    trace_id: string | null;
  };
};

// ── Authenticated actor ──────────────────────────────────────────────────────

export type Me = {
  user_id: string | null;
  actor_kind: string;
  email: string | null;
  display_name: string | null;
};

// ── Workspace users ──────────────────────────────────────────────────────────

export type WorkspaceUser = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  status: string;
};

export type WorkspaceUserListResponse = { items: WorkspaceUser[] };

export function formatUserName(u: WorkspaceUser): string {
  if (u.display_name) return u.display_name;
  const joined = [u.first_name, u.last_name].filter(Boolean).join(" ");
  return joined || u.email;
}

// ── Associations (Contact ↔ Account works_at) ────────────────────────────────

export type AccountLink = {
  association_id: string;
  account_id: string;
  account_name: string;
  account_primary_domain: string | null;
  is_primary: boolean;
  attributes: Record<string, unknown> | null;
  created_at: string;
};

export type ContactLink = {
  association_id: string;
  contact_id: string;
  contact_full_name: string;
  contact_primary_email: string | null;
  contact_title: string | null;
  is_primary: boolean;
  attributes: Record<string, unknown> | null;
  created_at: string;
};

export type ContactAccountCreate = {
  account_id: string;
  is_primary?: boolean;
  attributes?: Record<string, unknown> | null;
};

export type ContactAccountUpdate = {
  is_primary?: boolean | null;
  attributes?: Record<string, unknown> | null;
};

// ── Partner AI settings ─────────────────────────────────────────────────────

export type PartnerAISettingsRead = {
  id: string;
  partner_id: string;
  allowed_model_ids: string[] | null;
  default_model_id: string | null;
  monthly_inference_budget_cents: number | null;
  created_at: string;
  updated_at: string;
  version: number;
};

export type PartnerAISettingsUpdate = {
  allowed_model_ids?: string[] | null;
  default_model_id?: string | null;
  monthly_inference_budget_cents?: number | null;
};

// ── /v1/me/workspaces ───────────────────────────────────────────────────────

export type MyWorkspaceItem = {
  workspace_id: string;
  workspace_key: string;
  workspace_name: string;
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  partner_id: string;
  role_key: string;
  role_level: string;
  read_only: boolean;
  archived: boolean;
  membership_status: string;
  is_partner_admin: boolean;
};

export type MyWorkspacesResponse = {
  items: MyWorkspaceItem[];
};

// ── Notifications ───────────────────────────────────────────────────────────

export type NotificationRead = {
  id: string;
  workspace_id: string;
  user_id: string;
  kind: string;
  title: string;
  body: string | null;
  related_entity_type: string | null;
  // Bare ULID body — UI re-prefixes when building per-entity links.
  related_entity_id: string | null;
  link_path: string | null;
  channel: string;
  created_at: string;
  read_at: string | null;
};

export type NotificationListPage = {
  items: NotificationRead[];
  unread_count: number;
  next_cursor: string | null;
};

export type NotificationKindPref = {
  enabled: boolean;
  channels: string[];
};

export type NotificationPreferenceRead = {
  workspace_id: string;
  user_id: string;
  prefs: Record<string, NotificationKindPref>;
  updated_at: string | null;
};

export type NotificationPreferenceUpdate = {
  prefs: Record<string, NotificationKindPref>;
};

// ── Reports ─────────────────────────────────────────────────────────────────

export type PipelineByStageRow = {
  stage_id: string;
  stage_name: string;
  stage_order: number;
  stage_type: string;
  deal_count: number;
  total_amount: number;
  won_amount: number;
  open_amount: number;
};

export type PipelineByStageReport = {
  pipeline_id: string;
  generated_at: string;
  rows: PipelineByStageRow[];
};

export type DealsByOwnerRow = {
  owner_user_id: string | null;
  owner_label: string;
  open_count: number;
  won_count: number;
  lost_count: number;
  open_amount: number;
  won_amount: number;
};

export type DealsByOwnerReport = {
  generated_at: string;
  rows: DealsByOwnerRow[];
};

export type LeadScoreBand = {
  band: string;
  label: string;
  min_score: number | null;
  max_score: number | null;
  count: number;
};

export type LeadScoreDistributionReport = {
  generated_at: string;
  total_contacts: number;
  bands: LeadScoreBand[];
};
